/**
 * InsightsTracker
 * Lightweight, privacy-aware instrumentation for MVP telemetry.
 * Stores aggregate counters and a small rolling window of events in DataManager.
 */
export class InsightsTracker {
  constructor(dataManager) {
    this.data = dataManager;
    this.maxEvents = 120;
    this.ensureStores();
  }

  ensureStores() {
    if (!this.data?.ensureDefaults) {
      const counters = this.data?.get?.('_analyticsCounters');
      if (!counters) {
        this.data?.set?.('_analyticsCounters', {});
      }
      const events = this.data?.get?.('_analyticsEvents');
      if (!Array.isArray(events)) {
        this.data?.set?.('_analyticsEvents', []);
      }
      return;
    }

    this.data.ensureDefaults({
      _analyticsCounters: {},
      _analyticsEvents: [],
    });
  }

  track(eventName, payload = {}) {
    if (!eventName || !this.data) return;

    const timestamp = new Date().toISOString();

    // Update counters (no sensitive content).
    try {
      const counters = this.data.get('_analyticsCounters', {});
      counters[eventName] = (counters[eventName] || 0) + 1;
      this.data.set('_analyticsCounters', counters);
    } catch (error) {
      console.warn('[InsightsTracker] Failed to update counters', error);
    }

    // Store lightweight event metadata (name + timestamp + optional tags).
    const sanitized = this.sanitizePayload(payload);
    try {
      const events = this.data.get('_analyticsEvents', []);
      const next = Array.isArray(events) ? [...events, { name: eventName, timestamp, ...sanitized }] : [{
        name: eventName,
        timestamp,
        ...sanitized,
      }];
      if (next.length > this.maxEvents) {
        next.splice(0, next.length - this.maxEvents);
      }
      this.data.set('_analyticsEvents', next);
    } catch (error) {
      console.warn('[InsightsTracker] Failed to append event', error);
    }
  }

  sanitizePayload(payload) {
    if (!payload || typeof payload !== 'object') {
      return {};
    }

    const { tag, status, source, count } = payload;
    const sanitized = {};

    if (typeof tag === 'string') sanitized.tag = tag.slice(0, 60);
    if (typeof status === 'string') sanitized.status = status.slice(0, 60);
    if (typeof source === 'string') sanitized.source = source.slice(0, 60);
    if (typeof count === 'number') sanitized.count = count;

    return sanitized;
  }

  getCounters() {
    try {
      return this.data.get('_analyticsCounters', {});
    } catch (error) {
      console.warn('[InsightsTracker] Failed to read counters', error);
      return {};
    }
  }

  getEvents() {
    try {
      return this.data.get('_analyticsEvents', []);
    } catch (error) {
      console.warn('[InsightsTracker] Failed to read events', error);
      return [];
    }
  }
}
